import { InputSize } from "@amzn/meridian/input/input";
import { ResponsiveQuery } from "@amzn/meridian/responsive/responsive";
import { countries } from "../edit-document-modal/definitions";
import _ from "lodash";
import * as Yup from "yup";
import { assignedToOptions, stateOptions } from "../../features/filter-sheets/definitions";
import { yearBuiltOptions } from "../../utils/helperUtils";

export interface IDisplayFeaturesOrSpacesProps {
    featureDetails: Array<any>;
    spaceDetails: Array<any>;
    sheetName: string;
    handleEditUpdateSheetAndOpen: (...params: any) => void;
    handleDeleteItem: (...params: any) => void;
}

export enum PropertyParcelStatusEnum {
    ACTIVE = "Active",
    INACTIVE = "Inactive",
}

export enum OwnershipStatusEnum {
    LEASED = "Leased",
    OWNED = "Owned",
}

export interface IForm {
    label: string;
    formInputs: any;
    name?: string;
    progressState?: "past" | "present" | "future";
    progressAlert?: "error" | "warning" | "none";
    resetProgress?: boolean;
    validationSchema?: any;
    onSubmit?: (...params: any) => void;
}

export type FormType = IForm;

export interface IOtherImprovementsProps {
    inputHelperButtons: any;
    inputOptions: any;
    inputKey: any;
    fieldProps: any;
}

export const responsiveErrorToastHandler = (formValues, notificationHandler) => {
    notificationHandler({
        message: "Responsive form successfully submitted with errors",
        size: "large",
        type: "error",
        width: "50%",
    });
};

export const responsiveSuccessToastHandler = (formValues, notificationHandler) => {
    notificationHandler({
        message: `Property ${formValues} is created.`,
        size: "large",
        type: "success",
        width: "50%",
        timeout: 5000,
    });
};

export const updatedParcelSuccessToast = (parcelNumber, notificationHandler) => {
    notificationHandler({
        message: `Changes to Parcel ${parcelNumber} is saved`,
        size: "large",
        type: "success",
        width: "50%",
        timeout: 5000,
    });
};

export const successPropertyCreated = (SiteCode, notificationHandler) => {
    notificationHandler({
        message: `Property ${SiteCode} is created.`,
        size: "large",
        type: "success",
        width: "50%",
        timeout: 5000,
    });
};

export const successUpdatedToast = (SiteCode, notificationHandler) => {
    notificationHandler({
        message: `Changes to ${SiteCode} is saved.`,
        size: "large",
        type: "success",
        width: "50%",
        timeout: 5000,
    });
};

export const ResponsiveFormConfig: {
    responsiveProps: any;
    minWidthQuery: ResponsiveQuery;
    minHeightQuery: ResponsiveQuery;
} = {
    minWidthQuery: "min-width",
    minHeightQuery: "min-height",
    responsiveProps: {
        formConfig: {
            default: { spacer: false, wrap: "down", direction: "row", widths: ["100%", "100%"] },
            "200px": { spacer: false, wrap: "down", direction: "row", widths: ["100%", "100%"] },
            "620px": { spacer: true, wrap: "none", direction: "column", widths: ["35%", "60%"] },
            "1024px": { spacer: true, wrap: "none", direction: "column", widths: ["30%", "65%"] },
            "2000px": { spacer: true, wrap: "none", direction: "column", widths: ["30%", "60%"] },
        },
        widthTrackLength: {
            default: 8,
            "450px": 10,
            "500px": 12,
            "620px": 14,
        },
        heightTrackLength: {
            default: 3,
            "450px": 4,
            "600px": 6,
        },
    },
};

const largeInputSize: InputSize = "large";

export const defaultInputProps = {
    boxProps: {
        width: "stretch",
        spacingInset: "300 none",
        minWidth: "20px",
    },
    columnProps: {
        width: "stretch",
        minWidth: "20px",
        spacing: "300 none",
        alignmentVertical: "center",
    },
    rowProps: {
        spacing: "300 none",
        alignmentVertical: "center",
        width: "100%",
    },
};

const defaultSheetProps = {
    boxProps: {
        width: "100%",
        spacingInset: "300 none",
    },
    columnProps: {
        width: "100%",
        spacing: "300 none",
        alignmentVertical: "center",
    },
    rowProps: {
        spacing: "none",
        alignmentVertical: "center",
        alignmentHorizontal: "center",
        spacingInset: "none 200",
        width: "100%",
        widths: "fill",
    },
};

export const propertyFeatures = [
    {
        value: "Canopy",
        label: "Canopy",
        quantityLabel: "sqft",
        quantityUnit: "SQUARE FEET",
        placeholder: "Canopy Area (sqft)",
    },
    {
        value: "Cooling HVAC",
        label: "Cooling HVAC",
        quantityLabel: "sqft",
        quantityUnit: "SQUARE FEET",
        placeholder: "Cooling Area (sqft)",
    },
    {
        value: "Freight Elevator",
        label: "Freight Elevator",
        quantityLabel: "elevators",
        quantityUnit: "COUNT",
        placeholder: "Number of Freight Elevators",
    },
    {
        value: "Generator - Diesel",
        label: "Generator - Diesel",
        type: "diesel generators",
        quantityLabel: "diesel generators",
        quantityUnit: "COUNT",
        placeholder: "Number of Generator - Diesel",
    },
    {
        value: "Generator - Gas",
        label: "Generator - Gas",
        type: "gas generators",
        quantityLabel: "gas generators",
        quantityUnit: "COUNT",
        placeholder: "Number of Generator - Gas",
    },
    {
        value: "Guard House",
        label: "Guard House",
        quantityLabel: "sqft",
        quantityUnit: "SQUARE FEET",
        placeholder: "Guard Room Area (sqft)",
    },
    {
        value: "Mezzanine",
        label: "Mezzanine",
        quantityLabel: "sqft",
        quantityUnit: "SQUARE FEET",
        placeholder: "Mezzanine Area (sqft)",
    },
    {
        value: "Other",
        label: "Other",
        quantityLabel: "$",
        quantityUnit: "USD",
        placeholder: "Enter amount...",
    },
    {
        value: "Passenger Elevator",
        label: "Passenger Elevator",
        quantityLabel: "elevators",
        quantityUnit: "COUNT",
        placeholder: "Number of Passenger Elevators",
    },
    {
        value: "Sprinkler cost",
        label: "Sprinkler cost",
        quantityLabel: "sqft",
        quantityUnit: "SQUARE FEET",
        placeholder: "Sprinkler Area (sqft)",
    },
    {
        value: "Water Storage Tank",
        label: "Water Storage Tank",
        quantityLabel: "tanks",
        quantityUnit: "SQUARE FEET",
        placeholder: "Number of Water Tanks",
    },
];

export const CurbsDrainageLightingDefaultValue = {
    quantityUnit: "USD",
    quantity: 250000,
};

export const unitTypes = new Map([
    ["SQUARE FEET", { value: "SQUARE FEET", displayType: "sqft" }],
    ["FEET", { value: "FEET", displayType: "ft" }],
    ["INCH", { value: "INCH", displayType: "in" }],
    ["DOLLAR", { value: "USD", displayType: "$" }],
    ["USD", { value: "USD", displayType: "USD" }],
]);

export const unitTypesByKeysMap = new Map([
    ["buildingSizeUnit", unitTypes.get("SQUARE FEET")],
    ["landSizeUnit", unitTypes.get("SQUARE FEET")],
    ["avgFloorAreaUnit", unitTypes.get("SQUARE FEET")],
    ["avgStoryHeightUnit", unitTypes.get("FEET")],
    ["avgPerimeterUnit", unitTypes.get("FEET")],
    ["heightPerStoryUnit", unitTypes.get("FEET")],
    ["Asphalt", unitTypes.get("SQUARE FEET")],
    ["Concrete", unitTypes.get("SQUARE FEET")],
    ["Curbs, drainage, lighting", unitTypes.get("SQUARE FEET")],
    ["otherImprovements", unitTypes.get("DOLLAR")],
]);

export const mapKeysToUnitKeys = new Map([
    ["buildingSize", unitTypesByKeysMap.get("buildingSizeUnit")],
    ["landSize", unitTypesByKeysMap.get("landSizeUnit")],
    ["avgFloorArea", unitTypesByKeysMap.get("avgFloorAreaUnit")],
    ["avgStoryHeight", unitTypesByKeysMap.get("avgStoryHeightUnit")],
    ["avgPerimeter", unitTypesByKeysMap.get("avgPerimeterUnit")],
    ["heightPerStory", unitTypesByKeysMap.get("heightPerStoryUnit")],
]);

export const featuresQuantityUnitMap = new Map([
    ["Canopy", { featureName: "Canopy", quantityUnit: "SQUARE FEET", quantityLabel: "tanks" }],
    [
        "Cooling HVAC",
        { featureName: "Cooling HVAC ", quantityUnit: "SQUARE FEET", quantityLabel: "tanks" },
    ],
    [
        "Freight Elevator",
        { featureName: "Freight Elevator", quantityUnit: "COUNT", quantityLabel: "tanks" },
    ],
    [
        "Generator - Diesel",
        { featureName: "Generator - Diesel", quantityUnit: "COUNT", quantityLabel: "tanks" },
    ],
    [
        "Generator - Gas",
        { featureName: "Generator - Gas", quantityUnit: "COUNT", quantityLabel: "tanks" },
    ],
    [
        "Guard House",
        { featureName: "Guard House", quantityUnit: "SQUARE FEET", quantityLabel: "tanks" },
    ],
    [
        "Mezzanine",
        { featureName: "Mezzanine", quantityUnit: "SQUARE FEET", quantityLabel: "tanks" },
    ],
    ["Other", { featureName: "Other", quantityUnit: "USD", quantityLabel: "tanks" }],
    [
        "Passenger Elevator",
        { featureName: "Passenger Elevator", quantityUnit: "COUNT", quantityLabel: "tanks" },
    ],
    [
        "Sprinkler cost",
        { featureName: "Sprinkler cost", quantityUnit: "SQUARE FEET", quantityLabel: "tanks" },
    ],
    [
        "Water Storage Tank",
        { featureName: "Water Storage Tank", quantityUnit: "COUNT", quantityLabel: "tanks" },
    ],
]);

export const propertyQualityTypes = [
    { label: "Low", value: "Low" },
    { label: "Cheap", value: "Cheap" },
    { label: "Average", value: "Average" },
    { label: "Fair", value: "Fair" },
    { label: "Good", value: "Good" },
    { label: "Excellent", value: "Excellent" },
];

export const conditionTypes = [
    { label: "Average", value: "Average" },
    { label: "Poor", value: "Poor" },
    { label: "Good", value: "Good" },
    { label: "Very Good", value: "Very Good" },
    { label: "Excellent", value: "Excellent" },
];

export const propertySpaceSpaceType = [
    { value: "Retail Store", label: "Retail Store" },
    { value: "Office", label: "Office" },
    { value: "Supermarket", label: "Supermarket" },
    { value: "Industrial Flex", label: "Industrial Flex" },
    { value: "Data Center", label: "Data Center" },
    { value: "Distribution warehouse", label: "Distribution warehouse" },
    { value: "Cold Storage Facilities", label: "Cold Storage Facilities" },
    { value: "Mega warehouse", label: "Mega warehouse" },
    { value: "Storage", label: "Storage" },
    { value: "Mezzanine", label: "Mezzanine" },
    { value: "Hanger", label: "Hanger" },
    { value: "Parking Garage", label: "Parking Garage" },
    { value: "Light industrial Shell", label: "Light industrial Shell" },
];

export const propertyClassTypes = [
    { value: "A", label: "A" },
    { value: "B", label: "B" },
    { value: "C", label: "C" },
    { value: "D", label: "D" },
    { value: "S", label: "S" },
];

export const siteImprovementCheckboxOptions = [
    { value: "Asphalt", label: "Asphalt" },
    { value: "Concrete", label: "Concrete" },
    { value: "Curbs, drainage, lighting", label: "Curbs, drainage, lightning " },
    { value: "Other", label: "Other" },
];

export const propertySubTypeOptions = [
    {
        value: "Logistics Command Center & CS",
        label: "Logistics Command Center & CS",
    },
    { value: "AR Sortable FC Gen 8", label: "AR Sortable FC Gen 8" },
    {
        value: "AR Sortable Gen 11 FC-640G+4.5 (90' CH)",
        label: "AR Sortable Gen 11 FC-640G+4.5 (90' CH)",
    },
];

export const propertyTypeOptions = [
    { value: "AR Sortable", label: "AR Sortable" },
    { value: "Traditional Non-Sortable", label: "Traditional Non-Sortable" },
    { value: "Sort Center", label: "Sort Center" },
    { value: "Delivery Station", label: "Delivery Station" },
    { value: "Land", label: "Land" },
    { value: "Warehouse", label: "Warehouse" },
    { value: "AMZL", label: "AMZL" },
    { value: "Traditional Sortable", label: "Traditional Sortable" },
    { value: "Fulfillment Center", label: "Fulfillment Center" },
    { value: "Sortable FC", label: "Sortable FC" },
    { value: "Data Center", label: "Data Center" },
    { value: "Retail", label: "Retail" },
    { value: "FC IXD", label: "FC IXD" },
    { value: "Air Cargo Hub & Sortation", label: "Air Cargo Hub & Sortation" },
    { value: "Sub-Same Day", label: "Sub-Same Day" },
    { value: "Pickup Points", label: "Pickup Points" },
    { value: "AR Non-Sortable", label: "AR Non-Sortable" },
    { value: "Parking Lot", label: "Parking Lot" },
    { value: "Fresh FC", label: "Fresh FC" },
    { value: "Non-Sortable FC", label: "Non-Sortable FC" },
    { value: "Return Centers-Controllable", label: "Return Centers-Controllable" },
    { value: "Greenhouse", label: "Greenhouse" },
    { value: "Industrial", label: "Industrial" },
    { value: "Office", label: "Office" },
];

export const ownershipStatusOptions = [
    { value: "Leased", label: "Leased" },
    { value: "Owned", label: "Owned" },
    { value: "CTL", label: "CTL" },
];

export const statusOptions = [
    { value: "ACTIVE", label: "Active" },
    { value: "INACTIVE", label: "Inactive" },
];

export const climateOptions = [
    { label: "Moderate", value: "Moderate" },
    { label: "Extreme", value: "Extreme" },
];

export const regionOptions = [
    { label: "Western", value: "Western" },
    { label: "Eastern", value: "Eastern" },
    { label: "Central", value: "Central" },
];

export const areaUnitOptions = [
    { label: "Acres", value: "ACRES" },
    { label: "Square Feet", value: "SQUARE FEET" },
];

export interface IFormSectionInput {
    inputKey: string;
    fieldName: string;
    inputProps: any;
    inputType:
        | "standardInput"
        | "select"
        | "datePicker"
        | "groupCheckbox"
        | "groupButton"
        | "sheet"
        | "otherImprovements";
    boxProps?: any;
    columnProps?: any;
    rowProps?: any;
}

export const stateAbbreviations = [
    { value: "AK", label: "AK" },
    { value: "AL", label: "AL" },
    { value: "AR", label: "AR" },
    { value: "AZ", label: "AZ" },
    { value: "CA", label: "CA" },
    { value: "CO", label: "CO" },
    { value: "CT", label: "CT" },
    { value: "DE", label: "DE" },
    { value: "FL", label: "FL" },
    { value: "GA", label: "GA" },
    { value: "HI", label: "HI" },
    { value: "IA", label: "IA" },
    { value: "ID", label: "ID" },
    { value: "IL", label: "IL" },
    { value: "IN", label: "IN" },
    { value: "KS", label: "KS" },
    { value: "KY", label: "KY" },
    { value: "LA", label: "LA" },
    { value: "MA", label: "MA" },
    { value: "MD", label: "MD" },
    { value: "ME", label: "ME" },
    { value: "MI", label: "MI" },
    { value: "MN", label: "MN" },
    { value: "MO", label: "MO" },
    { value: "MS", label: "MS" },
    { value: "MT", label: "MT" },
    { value: "NC", label: "NC" },
    { value: "ND", label: "ND" },
    { value: "NE", label: "NE" },
    { value: "NH", label: "NH" },
    { value: "NJ", label: "NJ" },
    { value: "NM", label: "NM" },
    { value: "NV", label: "NV" },
    { value: "NY", label: "NY" },
    { value: "OH", label: "OH" },
    { value: "OK", label: "OK" },
    { value: "OR", label: "OR" },
    { value: "PA", label: "PA" },
    { value: "RI", label: "RI" },
    { value: "SC", label: "SC" },
    { value: "SD", label: "SD" },
    { value: "TN", label: "TN" },
    { value: "TX", label: "TX" },
    { value: "UT", label: "UT" },
    { value: "VA", label: "VA" },
    { value: "VT", label: "VT" },
    { value: "WA", label: "WA" },
    { value: "WI", label: "WI" },
    { value: "WV", label: "WV" },
    { value: "WY", label: "WY" },
    { value: "DC", label: "DC" },
    { value: "AS", label: "AS" },
    { value: "GU", label: "GU" },
    { value: "MP", label: "MP" },
    { value: "PR", label: "PR" },
    { values: "VI", label: "VI" },
];
export const addParcelConfig = {
    parcelNumber: {
        label: "Parcel number",
        inputs: [
            {
                fieldName: "parcelNumber",
                inputProps: {
                    placeholder: "Parcel number",
                    name: "parcelNumber",
                    size: largeInputSize,
                    disableInputOnCondition: {
                        condition: (pathName: string) => {
                            return pathName.split("/").splice(-1)[0] === "edit-parcel";
                        },
                    },
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "siteCode",
                inputProps: {
                    placeholder: "Site code",
                    name: "siteCode",
                    size: largeInputSize,
                    disableInputOnCondition: {
                        condition: (pathName: string) => {
                            return pathName.split("/").splice(-1)[0] === "edit-parcel";
                        },
                    },
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
        ],
    },
    address: {
        label: "Address",
        inputs: [
            {
                fieldName: "address.streetaddress",
                inputProps: {
                    placeholder: "Address Line 1",
                    name: "address.streetaddress",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "addressLineTwo",
                inputProps: {
                    placeholder: "Address line 2",
                    name: "address.addressLineTwo",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "address.city",
                inputProps: {
                    placeholder: "City",
                    name: "address.city",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "address.state",
                selectOptions: stateOptions,
                inputProps: {
                    placeholder: "State",
                    name: "address.state",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "address.zipCode",
                inputProps: {
                    placeholder: "Zip code",
                    name: "address.zipCode",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "address.county",
                inputProps: {
                    placeholder: "County name",
                    name: "address.county",
                    size: largeInputSize,
                    suffix: "county",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "address.country",
                selectOptions: [...countries],
                inputProps: {
                    placeholder: "Country",
                    name: "address.country",
                    size: largeInputSize,
                    suffix: "country",
                },
                ...defaultInputProps,
                inputType: "select",
            },
        ],
    },
    basicInformation: {
        label: "Basic information",
        inputs: [
            {
                fieldName: "parcelType",
                selectOptions: [
                    { value: "office", label: "office" },
                    { value: "AR Sortable", label: "AR Sortable" },
                ],
                inputProps: {
                    placeholder: "Parcel type",
                    name: "parcelType",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "parcelSubType",
                selectOptions: [
                    {
                        value: "Logistics Command Center & CS",
                        label: "Logistics Command Center & CS",
                    },
                    { value: "AR Sortable FC Gen 8", label: "AR Sortable FC Gen 8" },
                ],
                inputProps: {
                    placeholder: "Parcel sub type",
                    name: "parcelSubType",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "grossBuildingArea",
                inputProps: {
                    placeholder: "Gross building area",
                    name: "grossBuildingArea",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "grossBuildingAreaUnit",
                selectOptions: [...areaUnitOptions],
                inputProps: {
                    placeholder: "Gross building area unit",
                    name: "grossBuildingAreaUnit",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "grossLeasableArea",
                inputProps: {
                    placeholder: "Gross leasable area",
                    name: "grossLeasableArea",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "grossLeasableAreaUnit",
                selectOptions: [...areaUnitOptions],
                inputProps: {
                    placeholder: "Gross leasable area unit",
                    name: "grossLeasableAreaUnit",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "siteArea",
                inputProps: {
                    placeholder: "Site area",
                    name: "siteArea",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "siteAreaUnit",
                selectOptions: [...areaUnitOptions],
                inputProps: {
                    placeholder: "Site area unit",
                    name: "siteAreaUnit",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "siteUsableArea",
                inputProps: {
                    placeholder: "Site usable area",
                    name: "siteUsableArea",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "siteUsableAreaUnit",
                selectOptions: [...areaUnitOptions],
                inputProps: {
                    placeholder: "Site Usable area unit",
                    name: "siteUsableAreaUnit",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "yearBuilt",
                selectOptions: [...yearBuiltOptions],
                inputProps: {
                    placeholder: "Year built",
                    name: "yearBuilt",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
        ],
    },
    statusAndOwnership: {
        label: "Status and ownership",
        inputs: [
            {
                fieldName: "status",
                selectOptions: statusOptions,
                inputProps: {
                    placeholder: "Parcel status",
                    name: "status",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "ownershipStatus",
                selectOptions: [...ownershipStatusOptions],
                inputProps: {
                    placeholder: "Ownership status",
                    name: "ownershipStatus",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "entityName",
                inputProps: {
                    placeholder: "Entity name",
                    name: "entityName",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "entityCode",
                inputProps: {
                    placeholder: "Entity code",
                    name: "entityCode",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "secDivision",
                inputProps: {
                    placeholder: "Sec. division/group",
                    name: "secDivision",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "dateOpened",
                fieldName: "dateOpened",
                inputProps: {
                    placeholder: "Date opened",
                    name: "dateOpened",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "datePicker",
            },
            {
                inputKey: "dateClosed",
                fieldName: "dateClosed",
                inputProps: {
                    placeholder: "Date close/Lease end",
                    name: "dateClosed",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "datePicker",
            },
        ],
    },
    otherInformation: {
        label: "Other information",
        inputs: [
            {
                fieldName: "contacts",
                inputProps: {
                    placeholder: "Contacts",
                    name: "contacts",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "assignedTo",
                selectOptions: [...assignedToOptions],

                inputProps: {
                    placeholder: "assignedTo",
                    name: "assignedTo",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "otherDescription",
                inputProps: {
                    placeholder: "Property other description",
                    name: "otherDescription",
                    size: largeInputSize,
                    rows: 4,
                },
                ...defaultInputProps,
                inputType: "textArea",
            },
        ],
    },
    financialInformation: {
        label: "Financial information",
        inputs: [
            {
                fieldName: "saleDate",
                inputProps: {
                    placeholder: "Sale date",
                    name: "saleDate",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "datePicker",
            },
            {
                fieldName: "currency",
                inputProps: {
                    placeholder: "Currency",
                    name: "currency",
                    size: largeInputSize,
                },
                selectOptions: [{ value: "usd", label: "USD" }],
                ...defaultInputProps,
                inputType: "select",
            },
            {
                fieldName: "leaseRate",
                inputProps: {
                    placeholder: "Lease rate",
                    name: "leaseRate",
                    size: largeInputSize,
                    suffix: "/sqft/month",
                    prefix: "USD",
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "salePrice",
                inputProps: {
                    placeholder: "Sale price",
                    name: "salePrice",
                    size: largeInputSize,
                    prefix: "USD",
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "constructionCost",
                inputProps: {
                    placeholder: "Construction cost",
                    name: "constructionCost",
                    size: largeInputSize,
                    prefix: "USD",
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
        ],
    },
};

export const propertyAddressBasicValidation = Yup.object({
    propertyId: Yup.object({
        propertyNumber: Yup.string().required("Property number is missing"),
        siteCode: Yup.string().required("Address is missing"),
    }),
    address: Yup.object({
        streetaddress: Yup.string().required("Address is missing"),
        addressLineTwo: Yup.string().nullable(),
        city: Yup.string().required("City missing"),
        state: Yup.string().required("State must be selected"),
        zipCode: Yup.number().required("Zip code missing"),
        county: Yup.string().required("County name missing"),
        country: Yup.string().required("Country missing"),
    }),
    basicInformation: Yup.object({
        propertyType: Yup.string().required("Property type is missing"),
        propertySubType: Yup.string().nullable(),
        generalLedgerNumber: Yup.string().nullable(),
        grossBuildingArea: Yup.number().required("Gross building area missing"),
        grossBuildingAreaUnit: Yup.string().required("Gross building area unit missing"),
        grossLeasableArea: Yup.number().required("Gross lease area missing"),
        grossLeasableAreaUnit: Yup.string().required("Gross lease area unit missing"),
        siteArea: Yup.number().required("Site area missing"),
        siteAreaUnit: Yup.string().required("Site area unit missing"),
        siteUsableArea: Yup.number().required("Site area missing"),
        siteUsableAreaUnit: Yup.string().required("Site area unit missing"),
        yearBuilt: Yup.number().required("Year built missing"),
        effectiveYear: Yup.number().required("Effective Year missing"),
        region: Yup.string().required("Region missing"),
        climate: Yup.string().required("Climate missing"),
    }),
});

export const parcelAddressBasicValidation = Yup.object({
    parcelNumber: Yup.string().required("Parcel number is missing"),
    siteCode: Yup.string().required("Site code is missing"),
    address: Yup.object({
        streetaddress: Yup.string().required("Address is missing"),
        addressLineTwo: Yup.string().nullable(),
        city: Yup.string().required("City missing"),
        state: Yup.string().required("State must be selected"),
        zipCode: Yup.number().required("Zip code missing"),
        county: Yup.string().required("County name missing"),
        country: Yup.string().required("Country missing"),
    }),
    parcelType: Yup.string().required("Property type is missing"),
    parcelSubType: Yup.string().nullable(),
    grossBuildingArea: Yup.number().required("Gross building area missing"),
    grossBuildingAreaUnit: Yup.string().required("Gross building area unit missing"),
    grossLeasableArea: Yup.number().required("Gross lease area missing"),
    grossLeasableAreaUnit: Yup.string().required("Gross lease area unit missing"),
    siteArea: Yup.number().required("Site area missing"),
    siteAreaUnit: Yup.string().required("Site area unit missing"),
    siteUsableArea: Yup.number().required("Site usable area missing"),
    siteUsableAreaUnit: Yup.string().required("Site usable area unit missing"),
    yearBuilt: Yup.string().required("Year built missing"),
});

export const parcelStatusOwnershipOtherValidationSchema = Yup.object({
    status: Yup.string().required("Parcel status is missing"),
    ownershipStatus: Yup.string().nullable(),
    entityName: Yup.string().nullable(),
    entityCode: Yup.string().nullable(),
    secDivision: Yup.string().nullable(),
    dateOpened: Yup.string().nullable(),
    dateClosed: Yup.string().nullable(),
    contacts: Yup.string().nullable(),
    assignedTo: Yup.string().required("Assigned user is missing"),
    otherDescription: Yup.string().nullable("Property description is missing"),
});

export const parcelFinancialInformationValidationSchema = Yup.object({
    saleDate: Yup.string().nullable(),
    currency: Yup.string().required("Currency is missing"),
    leaseRate: Yup.string().nullable(),
    salePrice: Yup.number().nullable(),
    constructionCost: Yup.number().nullable(),
});

export const siteImprovementValidation = Yup.object({
    siteImprovementDetails: Yup.array().of(
        Yup.object({
            siteImprovementType: Yup.string().nullable(),
            siteImprovementName: Yup.string().when("siteImprovementType", {
                is: value => value.trim() === "Other",
                then: () => {
                    return Yup.string().required("Name is required");
                },
            }),
            isApplicable: Yup.boolean().nullable(),
            quantity: Yup.number().when("isApplicable", {
                is: value => !!value,
                then: () => {
                    return Yup.number().typeError("Required").required("Required");
                },
            }),
            quantityUnit: Yup.string().nullable(),
        })
    ),
    spaceDetails: Yup.object({
        addedSpaces: Yup.array()
            .of(Yup.object())
            .compact(space => !space.primary)
            .min(1, "At least one of the spaces should be a primary space.")
            .max(1, "More than 1 space is set as primary space. Max one allowed."),
    }),
});

export const statusOwnershipOtherValidationSchema = Yup.object({
    statusAndOwnership: Yup.object({
        status: Yup.string().required("Property status is missing"),
        ownershipStatus: Yup.string().nullable("Ownership status is missing"),
        entityName: Yup.string().nullable("Entity name is missing"),
        entityCode: Yup.string().nullable("Entity code is missing"),
        secDivision: Yup.string().nullable("Section div/group is missing"),
        dateOpened: Yup.string().nullable("Date opened is missing"),
        dateClosed: Yup.string().nullable("Date closed is missing"),
    }),
    otherInformation: Yup.object({
        contacts: Yup.string().nullable(),
        landlord: Yup.string().nullable(),
        assignedTo: Yup.string().required("Assigned user is missing"),
        otherDescription: Yup.string().nullable("Property description is missing"),
    }),
});

export const financialInformationValidationSchema = Yup.object({
    financialInformation: Yup.object({
        saleDate: Yup.string().nullable("Sale date is missing"),
        currency: Yup.string().nullable("Currency is missing").required("Currency is missing"),
        leaseRate: Yup.number().nullable("Lease rate is missing"),
        salePrice: Yup.number().nullable("Sale price is missing"),
        constructionCost: Yup.number().nullable("Construction cost is missing"),
    }),
});

export const addSpaceInitialValues = {
    addSpace: {
        spaceName: null,
        spaceType: null,
        buildingSize: null,
        status: "ACTIVE",
        primary: false,
        constructionClass: null,
        constructionQuality: null,
        noOfStories: null,
        heightPerStory: null,
        avgPerimeter: null,
        officePercentage: null,
        landSize: null,
        avgFloorArea: null,
        avgStoryHeight: null,
        condition: null,
        rentalClass: null,
    },
};

export const addFeatureInitialValues = {
    addFeature: {
        featureType: null,
        featureName: null,
        quantity: null,
    },
};

export const addSpaceValidationSchema = Yup.object({
    addSpace: Yup.object({
        spaceName: Yup.string().required("Space name is required"),
        spaceType: Yup.string().required("Space type is required"),
        buildingSize: Yup.number().typeError("Must be a number").required("Size is required"),
        status: Yup.string().required("Status is required"),
        constructionClass: Yup.string().required("Construction cost is required"),
        constructionQuality: Yup.string().required("Construction quality is required"),
        noOfStories: Yup.number()
            .typeError("Must be a number")
            .required("Number of stories is required"),
        heightPerStory: Yup.number().typeError("Must be a number"),
        avgFloorArea: Yup.number().typeError("Must be a number").required("Required"),
        avgPerimeter: Yup.number().typeError("Must be a number").required("Required"),
        avgStoryHeight: Yup.number().typeError("Must be a number").required("Required"),
        condition: Yup.string().required("Required"),
        landSize: Yup.number().typeError("Must be a number").required("Required"),
        rentalClass: Yup.string().required("Required"),
        officePercentage: Yup.number().typeError("Must be a number").required("Required"),
        occupancyTypeDescription: Yup.string().nullable(),
    }),
});

export const addFeatureValidationSchema = Yup.object({
    addFeature: Yup.object({
        featureType: Yup.string().required("Feature type is required"),
        featureName: Yup.string()
            .nullable()
            .when("featureType", {
                is: "Other",
                then: () => {
                    return Yup.string().required("Feature name is required");
                },
            }),
        quantity: Yup.number()
            .nullable()
            .when("featureType", {
                is: value => value.trim(),
                then: () => {
                    return Yup.number()
                        .typeError("Must be a number")
                        .required("Quantity is required");
                },
            }),
    }),
});

export const createPropertyInitialValues = {
    propertyId: {
        propertyNumber: null,
        siteCode: null,
    },
    address: {
        streetaddress: null,
        addressLineTwo: null,
        city: null,
        state: null,
        zipCode: null,
        county: null,
        country: null,
    },
    basicInformation: {
        propertyType: null,
        propertySubType: null,
        generalLedgerNumber: null,
        grossBuildingArea: null,
        grossLeasableArea: null,
        grossLeasableAreaUnit: null,
        siteArea: null,
        siteAreaUnit: null,
        siteUsableArea: null,
        siteUsableAreaUnit: null,
        yearBuilt: null,
        age: null,
        climate: null,
        effectiveYear: null,
        region: null,
    },
    statusAndOwnership: {
        status: null,
        ownershipStatus: null,
        entityName: null,
        entityCode: null,
        secDivision: null,
        dateOpened: null,
        dateClosed: null,
    },
    otherInformation: {
        contacts: null,
        landlord: null,
        assignedTo: null,
        otherDescription: null,
    },
    financialInformation: {
        saleDate: null,
        currency: null,
        leaseRate: null,
        salePrice: null,
        constructionCost: null,
    },
    spaceDetails: {
        addedSpaces: [],
    },
    featureDetails: {
        addedFeatures: [],
    },
    siteImprovementDetails: [
        {
            siteImprovementName: "Concrete",
            siteImprovementType: "Concrete",
            isApplicable: false,
            quantityUnit: "SQUARE FEET",
            quantity: 0,
        },
        {
            siteImprovementName: "Asphalt",
            siteImprovementType: "Asphalt",
            isApplicable: false,
            quantityUnit: "SQUARE FEET",
            quantity: 0,
        },
        {
            siteImprovementName: "Curbs, drainage, lighting",
            siteImprovementType: "Curbs, drainage, lighting",
            isApplicable: false,
            quantityUnit: "USD",
            quantity: 250000,
        },
    ],
};

export const siteImprovementDetailsEditInitialValues = [
    {
        siteImprovementName: "Concrete",
        siteImprovementType: "Concrete",
        isApplicable: false,
        quantityUnit: "SQUARE FEET",
        quantity: 0,
    },
    {
        siteImprovementName: "Asphalt",
        siteImprovementType: "Asphalt",
        isApplicable: false,
        quantityUnit: "SQUARE FEET",
        quantity: 0,
    },
    {
        siteImprovementName: "Curbs, drainage, lighting",
        siteImprovementType: "Curbs, drainage, lighting",
        isApplicable: false,
        quantityUnit: "USD",
        quantity: 250000,
    },
];

export const addParcelInitialValues = {
    parcelNumber: null,
    siteCode: null,
    address: {
        county: null,
        country: null,
        zipCode: null,
        streetaddress: null,
        state: null,
        city: null,
    },
    parcelType: null,
    parcelSubType: null,
    grossBuildingArea: null,
    grossBuildingAreaUnit: null,
    grossLeasableArea: null,
    grossLeasableAreaUnit: null,
    siteArea: null,
    siteAreaUnit: null,
    siteUsableArea: null,
    siteUsableAreaUnit: null,
    yearBuilt: null,
    status: null,
    ownershipStatus: null,
    entityName: null,
    entityCode: null,
    secDivision: null,
    dateOpened: null,
    dateClosed: null,
    contacts: null,
    assignedTo: null,
    saleDate: null,
    currency: null,
    leaseRate: null,
    salePrice: null,
    constructionCost: null,
};

/**
 *  Create property form config
 */
export const createPropertyConfig = {
    propertyId: {
        label: "Property ID",
        inputs: [
            {
                inputKey: "propertyNumber",
                fieldName: "propertyId.propertyNumber",
                inputProps: {
                    label: "Property number",
                    name: "propertyId.propertyNumber",
                    size: largeInputSize,
                    disableInputOnCondition: {
                        condition: (pathName: string) => {
                            return pathName.split("/").splice(-1)[0] === "edit-property";
                        },
                    },
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "siteCode",
                fieldName: "propertyId.siteCode",
                inputProps: {
                    label: "Site code",
                    name: "propertyId.siteCode",
                    size: largeInputSize,
                    disableInputOnCondition: {
                        condition: (pathName: string) => {
                            return pathName.split("/").splice(-1)[0] === "edit-property";
                        },
                    },
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
        ],
    },
    address: {
        label: "Address",
        inputs: [
            {
                inputKey: "streetaddress",
                fieldName: "address.streetaddress",
                inputProps: {
                    label: "Address Line 1",
                    name: "address.streetaddress",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "addressLineTwo",
                fieldName: "address.addressLineTwo",
                inputProps: {
                    label: "Address line 2",
                    name: "address.addressLineTwo",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "city",
                fieldName: "address.city",
                inputProps: {
                    label: "City",
                    name: "address.city",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "state",
                fieldName: "address.state",
                selectOptions: [...stateOptions],
                inputProps: {
                    label: "State",
                    name: "address.state",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "zipCode",
                fieldName: "address.zipCode",
                inputProps: {
                    label: "Zip code",
                    name: "address.zipCode",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "county",
                fieldName: "address.county",
                inputProps: {
                    label: "County name",
                    name: "address.county",
                    size: largeInputSize,
                    suffix: "county",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "country",
                fieldName: "address.country",
                selectOptions: countries,
                inputProps: {
                    label: "Country",
                    name: "address.country",
                    size: largeInputSize,
                    suffix: "country",
                },
                ...defaultInputProps,
                inputType: "select",
            },
        ],
    },
    basicInformation: {
        label: "Basic information",
        inputs: [
            {
                inputKey: "propertyType",
                fieldName: "basicInformation.propertyType",
                selectOptions: [...propertyTypeOptions],
                inputProps: {
                    label: "Property type",
                    name: "basicInformation.propertyType",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "propertySubType",
                fieldName: "basicInformation.propertySubType",
                selectOptions: [...propertySubTypeOptions],
                inputProps: {
                    label: "Property sub type",
                    name: "basicInformation.propertySubType",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "generalLedgerNumber",
                fieldName: "basicInformation.generalLedgerNumber",
                inputProps: {
                    label: "General ledger number",
                    name: "basicInformation.generalLedgerNumber",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "grossBuildingArea",
                fieldName: "basicInformation.grossBuildingArea",
                inputProps: {
                    label: "Gross building area",
                    name: "basicInformation.grossBuildingArea",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "basicInformation.grossBuildingAreaUnit",
                selectOptions: [...areaUnitOptions],
                inputProps: {
                    label: "Gross building area unit",
                    name: "basicInformation.grossBuildingAreaUnit",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "grossLeasableArea",
                fieldName: "basicInformation.grossLeasableArea",
                inputProps: {
                    label: "Gross leasable area",
                    name: "basicInformation.grossLeasableArea",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "basicInformation.grossLeasableAreaUnit",
                selectOptions: [...areaUnitOptions],
                inputProps: {
                    label: "Gross Leasable area unit",
                    name: "basicInformation.grossLeasableAreaUnit",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "siteArea",
                fieldName: "basicInformation.siteArea",
                inputProps: {
                    label: "Site area",
                    name: "basicInformation.siteArea",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "siteAreaUnit",
                fieldName: "basicInformation.siteAreaUnit",
                selectOptions: [...areaUnitOptions],
                inputProps: {
                    label: "Site area unit",
                    name: "basicInformation.siteAreaUnit",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "siteUsableArea",
                fieldName: "basicInformation.siteUsableArea",
                inputProps: {
                    label: "Site usable area",
                    name: "basicInformation.siteUsableArea",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                fieldName: "basicInformation.siteUsableAreaUnit",
                selectOptions: [...areaUnitOptions],
                inputProps: {
                    label: "Site usable area unit",
                    name: "basicInformation.siteUsableAreaUnit",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "yearBuilt",
                fieldName: "basicInformation.yearBuilt",
                selectOptions: [...yearBuiltOptions],
                inputProps: {
                    label: "Year built",
                    name: "basicInformation.yearBuilt",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "effectiveYear",
                fieldName: "basicInformation.effectiveYear",
                selectOptions: [...yearBuiltOptions],
                inputProps: {
                    label: "Effective year",
                    name: "basicInformation.effectiveYear",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "climate",
                fieldName: "basicInformation.climate",
                selectOptions: [...climateOptions],
                inputProps: {
                    label: "Climate",
                    name: "basicInformation.climate",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "region",
                fieldName: "basicInformation.region",
                selectOptions: [...regionOptions],
                inputProps: {
                    label: "Region",
                    name: "basicInformation.region",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
        ],
    },
    statusAndOwnership: {
        label: "Status and ownership",
        inputs: [
            {
                inputKey: "status",
                fieldName: "statusAndOwnership.status",
                selectOptions: statusOptions,
                inputProps: {
                    label: "Property status",
                    name: "statusAndOwnership.status",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "ownershipStatus",
                fieldName: "statusAndOwnership.ownershipStatus",
                selectOptions: [
                    { value: "Leased", label: "Leased" },
                    { value: "Owned", label: "Owned" },
                ],
                inputProps: {
                    label: "Ownership status",
                    name: "statusAndOwnership.ownershipStatus",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "entityName",
                fieldName: "statusAndOwnership.entityName",
                inputProps: {
                    label: "Entity name",
                    name: "statusAndOwnership.entityName",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "entityCode",
                fieldName: "statusAndOwnership.entityCode",
                inputProps: {
                    label: "Entity code",
                    name: "statusAndOwnership.entityCode",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "secDivision",
                fieldName: "statusAndOwnership.secDivision",
                inputProps: {
                    label: "Sec. division/group",
                    name: "statusAndOwnership.secDivision",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "dateOpened",
                fieldName: "statusAndOwnership.dateOpened",
                inputProps: {
                    label: "Date opened",
                    name: "statusAndOwnership.dateOpened",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "datePicker",
            },
            {
                inputKey: "dateClosed",
                fieldName: "statusAndOwnership.dateClosed",
                inputProps: {
                    label: "Date close/Lease end",
                    name: "statusAndOwnership.dateClosed",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "datePicker",
            },
        ],
    },
    otherInformation: {
        label: "Other information",
        inputs: [
            {
                inputKey: "contacts",
                fieldName: "otherInformation.contacts",
                inputProps: {
                    label: "Contacts",
                    name: "otherInformation.contacts",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "landlord",
                fieldName: "otherInformation.landlord",
                inputProps: {
                    label: "Landlord",
                    name: "otherInformation.landlord",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "assignedTo",
                fieldName: "otherInformation.assignedTo",
                selectOptions: [...assignedToOptions],

                inputProps: {
                    label: "Assign to user",
                    name: "otherInformation.assignedTo",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "otherDescription",
                fieldName: "otherInformation.otherDescription",
                inputProps: {
                    label: "Property other description",
                    name: "otherInformation.otherDescription",
                    size: largeInputSize,
                    rows: 4,
                },
                ...defaultInputProps,
                inputType: "textArea",
            },
        ],
    },
    financialInformation: {
        label: "Financial information",
        inputs: [
            {
                inputKey: "saleDate",
                fieldName: "financialInformation.saleDate",
                inputProps: {
                    label: "Sale date",
                    name: "financialInformation.saleDate",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "datePicker",
            },
            {
                inputKey: "currency",
                fieldName: "financialInformation.currency",
                selectOptions: [{ value: "USD", label: "USD" }],

                inputProps: {
                    label: "Currency",
                    name: "financialInformation.currency",
                    size: largeInputSize,
                },
                ...defaultInputProps,
                inputType: "select",
            },
            {
                inputKey: "leaseRate",
                fieldName: "financialInformation.leaseRate",
                inputProps: {
                    label: "Lease rate",
                    name: "financialInformation.leaseRate",
                    size: largeInputSize,
                    suffix: "/sqft/month",
                    prefix: "USD",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "salePrice",
                fieldName: "financialInformation.salePrice",
                inputProps: {
                    label: "Sale price",
                    name: "financialInformation.salePrice",
                    size: largeInputSize,
                    prefix: "USD",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
            {
                inputKey: "constructionCost",
                fieldName: "financialInformation.constructionCost",
                inputProps: {
                    label: "Lease rate",
                    name: "financialInformation.constructionCost",
                    size: largeInputSize,
                    prefix: "USD",
                },
                ...defaultInputProps,
                inputType: "standardInput",
            },
        ],
    },
    spaceDetails: {
        label: "Spaces",
        inputs: [
            {
                inputKey: "addedSpaces",
                fieldName: "spaceDetails.addedSpaces",
                sheetName: "addSpace",
                inputProps: {
                    label: "Add space",
                    size: "small",
                    type: "tertiary",
                    emptyState: "You haven't added any space for this property",
                },
                displayConfig: {},
                ...defaultInputProps,
                inputType: "sheet",
            },
        ],
    },
    featureDetails: {
        label: "Features",
        inputs: [
            {
                inputKey: "addedFeatures",
                fieldName: "featureDetails.addedFeatures",
                sheetName: "addFeature",
                inputProps: {
                    label: "Add feature",
                    emptyState: "You haven't added any feature for this property",
                    size: "small",
                    type: "tertiary",
                },
                displayConfig: {},
                inputType: "sheet",
            },
        ],
    },
    siteImprovementDetails: {
        label: "Site improvements",
        inputs: [
            {
                inputKey: "siteImprovementDetails",
                fieldName: "siteImprovementDetails",
                inputProps: {
                    name: "siteImprovementDetails",
                },
                ...defaultInputProps,
                inputType: "SiteImprovementDetails",
            },
        ],
    },
    addSpace: {
        inputs: [
            {
                fieldName: "addSpace.primary",
                inputProps: {
                    name: "addSpace.primary",
                },
                checkboxOptions: [{ value: true, label: "Primary space" }],
                ...defaultInputProps,
                inputType: "groupCheckbox",
            },
            {
                inputKey: "spaceName",
                fieldName: "addSpace.spaceName",
                inputProps: {
                    label: "Space name",
                    placeholder: "Enter space name",
                    name: "addSpace.spaceName",
                    size: largeInputSize,
                },
                inputType: "standardInput",
                ...defaultSheetProps,
            },

            {
                inputKey: "spaceType",
                fieldName: "addSpace.spaceType",
                selectOptions: [...propertySpaceSpaceType],
                inputProps: {
                    label: "Space type",
                    placeholder: "Select a type",
                    name: "addSpace.spaceType",
                    size: largeInputSize,
                },
                ...defaultSheetProps,
                inputType: "select",
            },
            {
                inputKey: "status",
                fieldName: "addSpace.status",
                groupButtonOptions: [
                    { value: "ACTIVE", label: "Active" },
                    { value: "INACTIVE", label: "Inactive" },
                ],
                inputProps: {
                    label: "Status",
                    name: "addSpace.status",
                    size: "medium",
                },
                ...defaultSheetProps,
                inputType: "groupButton",
            },
            {
                inputKey: "buildingSize",
                fieldName: "addSpace.buildingSize",
                inputProps: {
                    label: "Building size",
                    placeholder: "Enter space size...",
                    name: "addSpace.buildingSize",
                    size: largeInputSize,
                    suffix: "sqft",
                    inputMode: "numeric",
                },
                ...defaultSheetProps,
                inputType: "standardInput",
            },
            {
                inputKey: "avgFloorArea",
                fieldName: "addSpace.avgFloorArea",
                inputProps: {
                    label: "Average floor area",
                    name: "addSpace.avgFloorArea",
                    size: largeInputSize,
                    suffix: "sqft",
                    inputMode: "numeric",
                },
                ...defaultSheetProps,
                inputType: "standardInput",
            },

            {
                inputKey: "landSize",
                fieldName: "addSpace.landSize",
                inputProps: {
                    label: "Land size",
                    name: "addSpace.landSize",
                    size: largeInputSize,
                    suffix: "sqft",
                    inputMode: "numeric",
                },
                ...defaultSheetProps,
                inputType: "standardInput",
            },
            {
                inputKey: "avgPerimeter",
                fieldName: "addSpace.avgPerimeter",
                inputProps: {
                    label: "Enter Average Perimeter",
                    name: "addSpace.avgPerimeter",
                    size: largeInputSize,
                    suffix: "ft",
                    inputMode: "numeric",
                },
                ...defaultSheetProps,
                inputType: "standardInput",
            },
            {
                inputKey: "avgStoryHeight",
                fieldName: "addSpace.avgStoryHeight",
                inputProps: {
                    label: "Average story height",
                    name: "addSpace.avgStoryHeight",
                    size: largeInputSize,
                    suffix: "ft",
                    inputMode: "numeric",
                },
                ...defaultSheetProps,
                inputType: "standardInput",
            },
            {
                inputKey: "heightPerStory",
                fieldName: "addSpace.heightPerStory",
                inputProps: {
                    label: "Height per story",
                    name: "addSpace.heightPerStory",
                    size: largeInputSize,
                    inputMode: "numeric",
                    suffix: "ft",
                },
                ...defaultSheetProps,
                inputType: "standardInput",
            },
            {
                inputKey: "officePercentage",
                fieldName: "addSpace.officePercentage",
                inputProps: {
                    label: "Office percentage",
                    name: "addSpace.officePercentage",
                    size: largeInputSize,
                    suffix: "%",
                    inputMode: "numeric",
                },
                ...defaultSheetProps,
                inputType: "standardInput",
            },
            {
                inputKey: "constructionClass",
                fieldName: "addSpace.constructionClass",
                selectOptions: [...propertyClassTypes],
                inputProps: {
                    label: "Construction class",
                    placeholder: "Select a class",
                    name: "addSpace.constructionClass",
                    size: largeInputSize,
                },
                ...defaultSheetProps,
                inputType: "select",
            },

            {
                inputKey: "rentalClass",
                fieldName: "addSpace.rentalClass",
                selectOptions: [...propertyClassTypes],
                inputProps: {
                    label: "Rental class",
                    placeholder: "Select a class",
                    name: "addSpace.rentalClass",
                    size: largeInputSize,
                },
                ...defaultSheetProps,
                inputType: "select",
            },
            {
                inputKey: "condition",
                fieldName: "addSpace.condition",
                selectOptions: [...conditionTypes],
                inputProps: {
                    label: "Condition",
                    placeholder: "Select a class",
                    name: "addSpace.condition",
                    size: largeInputSize,
                },
                ...defaultSheetProps,
                inputType: "select",
            },
            {
                inputKey: "constructionQuality",
                fieldName: "addSpace.constructionQuality",
                selectOptions: [...propertyQualityTypes],
                inputProps: {
                    label: "Construction quality",
                    placeholder: "Select an option",
                    name: "addSpace.constructionQuality",
                    size: largeInputSize,
                },
                ...defaultSheetProps,
                inputType: "select",
            },

            {
                inputKey: "noOfStories",
                fieldName: "addSpace.noOfStories",
                inputProps: {
                    label: "Number of stories",
                    placeholder: "Enter number of stories...",
                    name: "addSpace.noOfStories",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultSheetProps,
                inputType: "standardInput",
            },

            {
                inputKey: "occupancyTypeDescription",
                fieldName: "addSpace.occupancyTypeDescription",
                inputProps: {
                    label: "Occupancy type description",
                    placeholder: "Enter description here...",
                    name: "addSpace.occupancyTypeDescription",
                    size: largeInputSize,
                },
                ...defaultSheetProps,
                inputType: "textArea",
            },
        ],
    },
    addFeature: {
        inputs: [
            {
                inputKey: "featureType",
                fieldName: "addFeature.featureType",
                selectOptions: [...propertyFeatures],
                inputProps: {
                    placeholder: "Feature type",
                    name: "addFeature.featureType",
                    size: largeInputSize,
                },
                ...defaultSheetProps,
                inputType: "select",
            },
            {
                inputKey: "featureName",
                fieldName: "addFeature.featureName",
                inputProps: {
                    hideInputOnCondition: {
                        // If field "siteImprovements.improvements" value does not include "Other"
                        isHideConditionTrue: currentForm => {
                            const featureTypeValue =
                                currentForm.getFieldProps("addFeature.featureType").value;

                            return !featureTypeValue?.includes("Other");
                        },
                    },
                    label: "Enter name of feature",
                    name: "addFeature.featureName",
                    size: largeInputSize,
                },
                ...defaultSheetProps,
                inputType: "standardInput",
            },
            {
                inputKey: "quantity",
                fieldName: "addFeature.quantity",
                inputProps: {
                    hideInputOnCondition: {
                        // If field "addFeature.featureType" value is not selected
                        isHideConditionTrue: currentForm => {
                            const featureTypeValue =
                                currentForm.getFieldProps("addFeature.featureType").value;

                            return !featureTypeValue?.trim();
                        },
                    },
                    getConditionalPlaceHolder: {
                        condition: currentForm => {
                            const featureTypeValue =
                                currentForm.getFieldProps("addFeature.featureType").value;

                            return propertyFeatures.filter(
                                feature => feature.value === featureTypeValue
                            )[0].placeholder;
                        },
                    },
                    placeholder: "Enter quantity...",
                    name: "addFeature.quantity",
                    size: largeInputSize,
                    inputMode: "numeric",
                },
                ...defaultSheetProps,
                inputType: "standardInput",
            },
        ],
    },
};
/**
 * CREATE EDIT PROPERTY FORM STEPS
 */
export const createPropertyFormSteps: Array<IForm> = [
    {
        onSubmit: () => void 0,
        formInputs: _.pick(createPropertyConfig, ["propertyId", "address", "basicInformation"]),
        label: "Property ID, address and basic information",
        progressState: "present",
        validationSchema: propertyAddressBasicValidation,
    },
    {
        formInputs: _.pick(createPropertyConfig, ["statusAndOwnership", "otherInformation"]),
        onSubmit: () => void 0,
        label: "Status, ownership and other information",
        validationSchema: statusOwnershipOtherValidationSchema,
    },
    {
        formInputs: _.pick(createPropertyConfig, ["financialInformation"]),
        onSubmit: () => void 0,
        label: "Financial information",
        validationSchema: financialInformationValidationSchema,
    },
    {
        formInputs: _.pick(createPropertyConfig, [
            "spaceDetails",
            "featureDetails",
            "siteImprovementDetails",
        ]),
        onSubmit: () => void 0,
        label: "Spaces, features and site improvements",
        validationSchema: siteImprovementValidation,
    },
];

export const addParcelFormSteps: Array<IForm> = [
    {
        formInputs: _.pick(addParcelConfig, [
            "parcelNumber",
            "siteCode",
            "address",
            "basicInformation",
        ]),
        onSubmit: () => void 0,
        label: "Address and basic information",
        progressState: "present",
        validationSchema: parcelAddressBasicValidation,
    },
    {
        formInputs: _.pick(addParcelConfig, ["financialInformation"]),
        onSubmit: () => void 0,
        label: "Financial information",
        validationSchema: parcelFinancialInformationValidationSchema,
    },
    {
        formInputs: _.pick(addParcelConfig, ["statusAndOwnership", "otherInformation"]),
        onSubmit: () => void 0,
        label: "Status, ownership and other information",
        validationSchema: parcelStatusOwnershipOtherValidationSchema,
    },
];
